import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
} from '@angular/core';
import { UserActivityClickTrackingDirective } from '@core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, map, tap } from 'rxjs/operators';
import { combineLatestWith, EMPTY, fromEvent, switchMap, take } from 'rxjs';
import { HeaderBackButtonComponent } from './back-button/header-back-button.component';

export interface PageHeaderBackSwipeConfig {
  element: HTMLElement;
  direction?: 'horizontal' | 'vertical'; // Direction of the swipe to trigger the back action
  threshold?: number; // Percentage of the element width to trigger the back action
  duration?: number; // Duration of the swipe effect
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  imports: [
    HeaderBackButtonComponent,
    UserActivityClickTrackingDirective,
  ],
  host: {
    'role': 'button',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements AfterViewInit {

  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  public readonly pageName = input.required<string>();

  public readonly backButtonClick = output();
  public readonly backSwipe = input<PageHeaderBackSwipeConfig>();
  public readonly backSwipe$ = toObservable(this.backSwipe);

  public ngAfterViewInit() {
    const backButton = this.elementRef.nativeElement.getElementsByClassName('back')[0] as HTMLButtonElement;

    this.backSwipe$.pipe(
      switchMap((config) => {
        if (!config) {
          return EMPTY;
        }

        return fromEvent<TouchEvent>(config.element, 'touchstart').pipe(
          map((touchstart) => (
            {
              config,
              touchstart,
            }
          )),
        );
      }),
      filter(({ config, touchstart }) => {
        const threshold = Math.max(Math.min(1, config.threshold ?? 0.1), 0.01);

        if (config.direction === 'vertical') {
          return touchstart.touches[0].pageY - config.element.clientTop < threshold * config.element.clientHeight;
        }

        return touchstart.touches[0].pageX - config.element.clientLeft < threshold * config.element.clientWidth;
      }),
      switchMap(({ config, touchstart }) => fromEvent<TouchEvent>(config.element, 'touchmove').pipe(
        filter((touchmove) => touchstart.timeStamp <= touchmove.timeStamp),
        map((touchmove) => {
          if (config.direction === 'vertical') {
            return touchmove.touches[0].clientY - touchstart.touches[0].clientY;
          }

          return touchmove.touches[0].clientX - touchstart.touches[0].clientX;
        }),
        filter((distance) => distance > 0),
        tap((distance) => {
          const clientSize = config.direction === 'vertical' ? config.element.clientHeight : config.element.clientWidth;

          const scale = Math.max(
            1,
            1 + (
              distance / clientSize
            ) * 2,
          );
          const opacity = Math.max(
            0.1,
            1 - (
              distance / clientSize
            ),
          );
          backButton.setAttribute('style', `transform: scale(${ scale }); opacity: ${ opacity };`);
        }),
        combineLatestWith(fromEvent<MouseEvent>(config.element, 'touchend')),
        take(1),
        map(([distance, touchend]) => (
          {
            config,
            touchstart,
            distance,
            touchend,
          }
        )),
      )),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ config, distance }) => {
      const clientSize = config.direction === 'vertical' ? config.element.clientHeight : config.element.clientWidth;
      const duration = Math.max(Math.min(1, config.duration ?? 0.5), 0.1);

      if (distance > duration * clientSize) {
        backButton.click();
      }

      backButton.animate([{ transform: 'scale(1)' }], {
        duration: 300,
        easing: 'ease-in-out',
      }).onfinish = () => {
        backButton.setAttribute('style', `transform: scale(1)`);
      };
    });
  }

}
